// The last state this browser saw for a unit, so the board can paint real
// numbers instead of the word "loading".
//
// No interface can draw an unfetched number. The honest alternative to a
// spinner is the last figures we had, said out loud to be the last figures we
// had — never presented as current, and never sitting under a control that
// commits against them without their age being legible in the same glance.

import type { MuState } from "@shared/types";

const KEY = "muBoardState:";

export interface CachedState {
  state: MuState;
  /** When this browser stored it, epoch-ms. The age the reader is shown is
   * measured from here, not from anything the payload says about itself. */
  storedAt: number;
}

export function readCachedState(muId: string): CachedState | null {
  try {
    const raw = localStorage.getItem(KEY + muId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedState;
    // A cache written by an older build can be missing fields the page now
    // reads. Anything that does not carry a unit id is not a state.
    if (!parsed?.state?.muId || typeof parsed.storedAt !== "number") return null;
    return parsed;
  } catch {
    return null; // storage refused, or the entry is not ours to read
  }
}

export function writeCachedState(muId: string, state: MuState, nowMs: number): void {
  try {
    localStorage.setItem(KEY + muId, JSON.stringify({ state, storedAt: nowMs }));
  } catch {
    // Private browsing, a full quota, or blocked storage. The page still works;
    // it just starts cold each visit, which is what it did before.
  }
}

export function dropCachedState(muId: string): void {
  try {
    localStorage.removeItem(KEY + muId);
  } catch {
    // Nothing to do: the entry was unreadable anyway.
  }
}
